import { useState, useCallback, useEffect } from 'react';
import { Table, TableHeader, TableBody, Column, Row, Label } from 'react-aria-components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale/ja';
import { useAtom } from 'jotai';
import * as yup from 'yup';

import { createFacilityCalendars } from '@/api/createFacilityCalendars';
import { getOffices } from '@/api/getOffices';
import Breadcrumbs, { BreadcrumbItem } from '@/components/Breadcrumbs/Breadcrumbs';
import Button from '@/components/Button/Button';
import FileUpload from '@/components/FileUpload/FileUpload';
import SelectField from '@/components/SelectField/SelectField';
import { NotificationAtom } from '@/jotai/jotai';
import { MonthlyCalendarItem } from '@/types/monthlyCalendar';
import { Office } from '@/types/office';

type FormData = {
  office_id: string;
};

export const MonthlyCalendarPage = () => {
  const [monthlyCalendarItems, setMonthlyCalendarItems] = useState<MonthlyCalendarItem[]>([]);
  const email = localStorage.getItem('email') || '';
  const { pathname: pathName } = useLocation();
  const breadcrumbs: BreadcrumbItem[] = [{ path: pathName, name: '月間カレンダー登録' }];
  const [offices, setOffices] = useState<Office[]>([]);

  const [_, setNotification] = useAtom(NotificationAtom);

  useEffect(() => {
    const fetchOffices = async () => {
      const result = await getOffices(email);
      if (result.status === 200) {
        setOffices(result.result);
      }
    };
    fetchOffices();
  }, []);

  const yupSchema: yup.ObjectSchema<FormData> = yup.object({
    office_id: yup.string().required('事業所を選択してください'),
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: offices.length > 0 ? { office_id: offices[0].id.toString() } : {},
    resolver: yupResolver(yupSchema),
  });

  const handleCSVUpload = (file: File | undefined) => {
    if (file === undefined) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: 'ファイルが選択されていません',
        message: 'ファイルが選択されていません',
      });
      return;
    }
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => {
      const csv = reader.result as string;
      const csvArray = csv.split('\n');

      const expectedHeader =
        '年,月,日,休業日,イベント名,イベント内容（内容、目的等）,夕食提供,夕食メニュー';
      if (csvArray[0].trim() !== expectedHeader) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: 'ヘッダー行が不正です',
          message:
            '「年,月,日,休業日,イベント名,イベント内容（内容、目的等）,夕食提供,夕食メニュー」の順で入力してください',
        });
        return;
      }

      // ヘッダー行と空行を除外
      const dataRows = csvArray.slice(1).filter((line) => line.trim() !== '');

      if (dataRows.length === 0) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: 'データがありません',
          message: 'CSVファイルにデータが含まれていません',
        });
        return;
      }

      const items = dataRows.map((line) => {
        const [
          year,
          month,
          day,
          isHoliday,
          eventTitle,
          eventDescription,
          isDinnerAvailable,
          dinnerMenu,
        ] = line.split(',');
        return {
          office_id: 0, // 後から上書きする
          year: parseInt(year),
          month: parseInt(month),
          day: parseInt(day),
          is_holiday: isHoliday === '休業日',
          event_title: eventTitle,
          event_description: eventDescription,
          is_dinner: isDinnerAvailable === 'あり',
          dinner_menu: dinnerMenu,
        };
      });
      setMonthlyCalendarItems(items);
    };
  };

  const handleConfirm: SubmitHandler<FormData> = useCallback(
    async (data) => {
      if (monthlyCalendarItems.length === 0) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: 'csvファイルをアップロードしてください',
          message: 'csvファイルをアップロードしてください',
        });
        return;
      }
      try {
        monthlyCalendarItems.forEach((item) => {
          item.office_id = parseInt(data.office_id);
        });
        const result = await createFacilityCalendars(monthlyCalendarItems);
        if (result.status === 201) {
          setNotification({
            headingLevel: 'h2',
            type: 'success',
            bannerStyle: 'standard',
            title: 'カレンダーを登録しました',
            message: 'カレンダーを登録しました',
          });
        } else {
          setNotification({
            headingLevel: 'h2',
            type: 'error',
            bannerStyle: 'standard',
            title: 'カレンダーの登録に失敗しました',
            message: 'カレンダーの登録に失敗しました',
          });
        }
      } catch (error) {
        setNotification({
          headingLevel: 'h2',
          type: 'error',
          bannerStyle: 'standard',
          title: 'カレンダーの登録に失敗しました',
          message: 'カレンダーの登録に失敗しました',
        });
      }
    },
    [monthlyCalendarItems]
  );

  const dayOfWeek = (year: number, month: number, day: number) => {
    return format(new Date(year, month - 1, day), 'E', { locale: ja });
  };

  const DinnerAvailabilityChip = ({ isDinner }: { isDinner: boolean }) => {
    return (
      <div
        className={`inline-flex items-center justify-center rounded rounded-full border px-2 py-1 ${isDinner ? 'bg-red-700' : 'bg-gray-700'}`}
      >
        <span className="rounded px-1 text-xs text-white">{isDinner ? 'あり' : 'なし'}</span>
      </div>
    );
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-row">
        <Breadcrumbs item={breadcrumbs} />
      </div>
      <div className="my-4 mb-4 flex flex-col gap-2">
        {monthlyCalendarItems && monthlyCalendarItems.length > 0 && (
          <h1 className="mb-2 text-xl font-bold">{`${monthlyCalendarItems[0].year}年${monthlyCalendarItems[0].month}月`}</h1>
        )}
        <span className="text-sm">※長期休暇中は夕食提供ではなく、昼食の提供となります。</span>
      </div>
      <div className="mb-2 flex flex-row items-center justify-end gap-4">
        <a
          href="/calendar/template.csv"
          download
          className="text-blue-500 underline"
        >
          テンプレートダウンロード
        </a>

        <div className="flex flex-row items-center justify-center gap-4">
          <Label className="mb-2 w-max text-base md:text-lg md:font-bold">事業所</Label>
          <div className="flex flex-col items-center justify-center gap-4">
            <SelectField
              name="office_id"
              control={control}
              items={offices.map((office) => ({
                label: office.name,
                value: office.id.toString(),
              }))}
            />
            {errors.office_id && (
              <span className="text-sm text-red-500">{errors.office_id.message}</span>
            )}
          </div>
        </div>

        <FileUpload
          buttonText="CSVアップロード"
          accept="text/csv"
          setFile={handleCSVUpload}
        />
        <Button
          size="md"
          variant="solid-fill"
          onClick={handleSubmit(handleConfirm)}
        >
          確定
        </Button>
      </div>
      <div>
        <Table className="w-full border-collapse border border-gray-200">
          <TableHeader>
            <Column
              isRowHeader
              className="border border-gray-200 bg-gray-50 px-4 py-2"
            >
              {`${monthlyCalendarItems && monthlyCalendarItems.length > 0 ? monthlyCalendarItems[0].month : ''}月`}
            </Column>
            <Column
              isRowHeader
              className="border border-gray-200 bg-gray-50 px-4 py-2"
            >
              曜日
            </Column>
            <Column className="border border-gray-200 bg-gray-50 px-4 py-2">休業日</Column>
            <Column className="border border-gray-200 bg-gray-50 px-4 py-2">イベント名</Column>
            <Column className="border border-gray-200 bg-gray-50 px-4 py-2">
              イベント内容（内容、目的等）
            </Column>
            <Column className="border border-gray-200 bg-gray-50 px-4 py-2">夕食提供</Column>
            <Column className="border border-gray-200 bg-gray-50 px-4 py-2">夕食メニュー</Column>
          </TableHeader>
          <TableBody>
            {monthlyCalendarItems &&
              monthlyCalendarItems.map((monthlyCalendarItem, index) => (
                <Row
                  key={index}
                  className={`${monthlyCalendarItem.is_holiday ? 'bg-gray-100' : ''}`}
                >
                  <Column
                    key="day"
                    className="border border-gray-200 px-4 py-2"
                  >
                    {monthlyCalendarItem.day}
                  </Column>
                  <Column
                    key="dayOfWeek"
                    className="border border-gray-200 px-4 py-2"
                  >
                    {dayOfWeek(
                      monthlyCalendarItem.year,
                      monthlyCalendarItem.month,
                      monthlyCalendarItem.day
                    )}
                  </Column>
                  <Column
                    key="isHoliday"
                    className="border border-gray-200 px-4 py-2"
                  >
                    {monthlyCalendarItem.is_holiday ? '休業日' : ''}
                  </Column>
                  <Column
                    key="eventTitle"
                    className="border border-gray-200 px-4 py-2"
                  >
                    {monthlyCalendarItem.event_title}
                  </Column>
                  <Column
                    key="eventDescription"
                    className="border border-gray-200 px-4 py-2"
                  >
                    {monthlyCalendarItem.event_description}
                  </Column>
                  <Column
                    key="isDinnerAvailable"
                    className="border border-gray-200 px-4 py-2"
                  >
                    <DinnerAvailabilityChip isDinner={monthlyCalendarItem.is_dinner} />
                  </Column>
                  <Column
                    key="dinnerMenu"
                    className="border border-gray-200 px-4 py-2"
                  >
                    {monthlyCalendarItem.dinner_menu}
                  </Column>
                </Row>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
